



import { decimalFormat } from '@/helpers/decimalFormat';
import StringUtils from '@/helpers/stringUtils';
import { JsonException } from '@/interfaces/base-dto';
import { checkUserPermissions } from '@/services/auth';
import { getLocalUser } from '@/services/session-data';
import { PERMISSIONS_KEYS } from '@/settings/consts/permissions';
import { BAR_COLOR } from '@/settings/consts/theme';
import { ACCESS_TYPE_CLIENT, ACCESS_TYPE_EMEDE, USER_TYPE_DOCTOR, USER_TYPE_OTHER } from '@/settings/consts/users';
import { AxiosResponse } from 'axios';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, skipWhile, switchMap, tap } from 'rxjs/operators';
import Vue from 'vue';
import Component from 'vue-class-component';
import { dateFormat } from '../helpers/dateFormat';
import * as dialog from '../subjects/dialog';
import * as loader from '../subjects/loader';
import * as toast from '../subjects/toast';

@Component
export default class BaseComponent extends Vue {
  public loader = loader;
  public dialog = dialog;
  public toast = toast;

  public permissionsKeys = PERMISSIONS_KEYS;

  static currentUser: any = {};
  static currentUserObservable = null;

  public subscriptions: Subscription[] = [];

  public barColor: string = BAR_COLOR;

  public rows_per_page_items = [25, 50, 100];

  constructor() {
    super();
    if (BaseComponent.currentUserObservable == null) {
      BaseComponent.currentUserObservable = getLocalUser();
      BaseComponent.currentUserObservable.subscribe((data) => {
        if (!!data) {
          BaseComponent.currentUser = data;
        }
      });
    }
  }

  public hasPermission(perm) {
    return checkUserPermissions(perm);
  }

  public dateFormatHelper(date, format) {
    if (!date) {
      return null;
    }
    return dateFormat(date, format);
  }

  public decimalFormatHelper(val, decimalPlaces: any = 2) {
    return decimalFormat(val, decimalPlaces);
  }

  public isEmede(): boolean {
    return BaseComponent.currentUser.tipoAcesso === ACCESS_TYPE_EMEDE;
  }

  public isDoctor(): boolean {
    return BaseComponent.currentUser.tipoLogin === USER_TYPE_DOCTOR;
  }

  public isClientUserNonDoctor(): boolean {
    return (
      BaseComponent.currentUser.tipoAcesso === ACCESS_TYPE_CLIENT &&
      BaseComponent.currentUser.tipoLogin === USER_TYPE_OTHER
    );
  }

  public addSubsc(subscription: Subscription): void {
    this.subscriptions.push(subscription);
  }

  private unsubscribeAll() {
    this.subscriptions.forEach((s) => {
      s.unsubscribe();
    });
  }

  public beforeDestroy() {
    this.unsubscribeAll();
  }

  public async sleepUntil(f, timeoutMs) {
    return new Promise<void>((resolve, reject) => {
      let timeWas = new Date().getTime();
      let wait = setInterval(function () {
        if (f()) {
          console.log('resolved after', new Date().getTime() - timeWas, 'ms');
          clearInterval(wait);
          resolve();
        } else if (new Date().getTime() - timeWas > timeoutMs) {
          // Timeout
          console.log('rejected after', new Date().getTime() - timeWas, 'ms');
          clearInterval(wait);
          reject();
        }
      }, 50);
    });
  }

  public forceFileAxiosResponseDownload(response: AxiosResponse, defaultFilename: string) {
    let contentDisposition = String(response.headers['content-disposition']);
    let filename = defaultFilename;
    if (contentDisposition && contentDisposition.indexOf('filename') != -1) {
      filename = contentDisposition
        .split(';')
        .filter((f) => f.indexOf('filename') != -1)[0]
        .split('=')[1]
        .trim();
    }
    this.forceFileBlobDownload(response.data, filename);
  }

  public forceFileBlobDownload(response: Blob, filename) {
    const url = window.URL.createObjectURL(response);
    this.downloadLink(url, filename, null);
  }

  public downloadLink(url, filename, callAfterDownload) {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    if (callAfterDownload) {
      callAfterDownload();
    }
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }

  public showToastApiError(error) {
    if (error && error.data) {
      if (error.data instanceof Blob) {
        error.data.text().then((t) => {
          this.toast.show(JSON.parse(t).message.replace(/\n/g, '</br>'), 'error', true, 8000);
        });
      } else if (!!error.data.message) {
        this.toast.show(error.data.message.replace(/\n/g, '</br>'), 'error', true, 8000);
      } else {
        this.toast.show(error.data.replace(/\n/g, '</br>'), 'error', true, 8000);
      }
    } else {
      this.toast.show(error, 'error', true, 8000);
    }
  }

  isJsonExceptionTypeError(error, className: string) {
    if (StringUtils.isValidJSONString(error.data.message)) {
      let obj: JsonException = JSON.parse(error.data.message);
      return obj.className && obj.className == className;
    }
    return false;
  }

  parseJsonExceptionTypeOrNull(error, classNameCompare: string, typeCast: JsonException) {
    if (!this.isJsonExceptionTypeError(error, classNameCompare)) {
      return null;
    }
    let obj = JSON.parse(error.data.message);
    return Object.assign(typeCast, obj);
  }

  defaultCustomFilter(item, queryText, itemText) {
    return this.componentFilter(itemText, queryText);
  }

  public componentFilter(values: any[], queryText): boolean {
    const searchText = queryText
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    let found = false;
    if (!(values instanceof Array)) {
      let array = [];
      array.push(values);
      values = array;
    }
    values.forEach((v) => {
      if (!v) {
        return;
      }
      v = String(v).toString();
      const text = v
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      if (text.indexOf(searchText) > -1) {
        found = true;
        return;
      }
    });
    return found;
  }

  buildBehaviorSubject(
    serviceCallFunction: Function,
    tapFunction: Function,
    debounceTimeValue: number,
    nextFunction: Function,
    errorFunction: Function
  ): BehaviorSubject<any> {
    let bs = new BehaviorSubject(null);
    bs.pipe(
      skipWhile((val) => {
        return val === null;
      }),
      debounceTime(debounceTimeValue),
      tap(() => {
        tapFunction();
      }),
      switchMap((filter) => {
        return serviceCallFunction(filter);
      })
    ).subscribe(
      (res) => {
        nextFunction(res);
      },
      (error) => {
        errorFunction(error);
      }
    );
    return bs;
  }
}
